import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useBudget } from '../../contexts/BudgetContext';
import { useAccount } from '../../contexts/AccountContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import LockedFeature from '../Subscription/LockedFeature';
import api from '../../services/api';

const KINDS = ['bill', 'subscription', 'debt', 'one_off', 'split'];
const RECURS = ['once', 'weekly', 'monthly', 'quarterly', 'yearly'];

const STATUS_ORDER = { overdue: 0, due: 1, upcoming: 2, paid: 3 };

const coverageSx = {
  green: { bg: 'rgba(16,185,129,0.10)', fg: '#059669', border: 'rgba(16,185,129,0.3)' },
  amber: { bg: 'rgba(245,158,11,0.10)', fg: '#d97706', border: 'rgba(245,158,11,0.35)' },
  red: { bg: 'rgba(239,68,68,0.10)', fg: '#dc2626', border: 'rgba(239,68,68,0.35)' },
};

const statusChip = {
  overdue: { color: 'error', labelKey: 'payables.overdue' },
  due: { color: 'warning', labelKey: 'payables.dueSoon' },
  upcoming: { color: 'info', labelKey: 'payables.upcoming' },
  paid: { color: 'success', labelKey: 'payables.paid' },
};

const blankForm = {
  name: '',
  amount: '',
  kind: 'bill',
  recurrence: 'monthly',
  dueDay: new Date().getDate(),
  dueOn: '',
  categoryId: '',
  accountId: '',
  notes: '',
  autopay: false,
};

function monthLabel(year, month, locale) {
  return new Date(year, month - 1, 1).toLocaleDateString(locale || undefined, {
    month: 'long',
    year: 'numeric',
  });
}

function catId(c) {
  return c._id || c.id;
}

const PayableFormDialog = ({
  open, onClose, onSave, initial, categories, accounts, t,
}) => {
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    if (initial) {
      setForm({
        name: initial.name || '',
        amount: initial.amount ?? '',
        kind: initial.kind || 'bill',
        recurrence: initial.recurrence || 'monthly',
        dueDay: initial.dueDay || (initial.dueOn ? new Date(initial.dueOn).getDate() : 1),
        dueOn: initial.dueOn ? new Date(initial.dueOn).toISOString().slice(0, 10) : '',
        categoryId: initial.categoryId || '',
        accountId: initial.accountId || '',
        notes: initial.notes || '',
        autopay: Boolean(initial.autopay),
        _id: initial._id,
      });
    } else {
      setForm(blankForm);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setError('');
    if (!String(form.name).trim()) {
      setError(t('payables.nameRequired', 'Name is required'));
      return;
    }
    if (!(Number(form.amount) > 0)) {
      setError(t('payables.amountRequired', 'Amount must be greater than 0'));
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...form,
        amount: Number(form.amount),
        dueDay: Number(form.dueDay) || 1,
        categoryId: form.categoryId || null,
        accountId: form.accountId || null,
        dueOn: form.recurrence === 'once' ? form.dueOn : form.dueOn || null,
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Save failed');
      setSaving(false);
      return;
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {form._id ? t('payables.edit', 'Edit payable') : t('payables.add', 'Add payable')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('payables.name', 'Name')}
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            fullWidth
            required
            autoFocus
          />
          <TextField
            label={t('payables.amount', 'Amount')}
            type="number"
            value={form.amount}
            onChange={(e) => set('amount', e.target.value)}
            fullWidth
            required
            inputProps={{ min: 0, step: '0.01' }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>{t('payables.kind', 'Type')}</InputLabel>
              <Select
                label={t('payables.kind', 'Type')}
                value={form.kind}
                onChange={(e) => set('kind', e.target.value)}
              >
                {KINDS.map((k) => (
                  <MenuItem key={k} value={k}>{t(`payables.kinds.${k}`, k)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('payables.recurrence', 'Repeats')}</InputLabel>
              <Select
                label={t('payables.recurrence', 'Repeats')}
                value={form.recurrence}
                onChange={(e) => set('recurrence', e.target.value)}
              >
                {RECURS.map((k) => (
                  <MenuItem key={k} value={k}>{t(`payables.recurs.${k}`, k)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {form.recurrence === 'once' ? (
            <TextField
              label={t('payables.dueOn', 'Due date')}
              type="date"
              value={form.dueOn}
              onChange={(e) => set('dueOn', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          ) : (
            <TextField
              label={t('payables.dueDay', 'Due day')}
              type="number"
              value={form.dueDay}
              onChange={(e) => set('dueDay', e.target.value)}
              inputProps={{ min: 1, max: 31 }}
              fullWidth
            />
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>{t('payables.category', 'Category')}</InputLabel>
              <Select
                label={t('payables.category', 'Category')}
                value={form.categoryId || ''}
                onChange={(e) => set('categoryId', e.target.value)}
              >
                <MenuItem value="">{t('payables.none', 'None')}</MenuItem>
                {(categories || []).map((c) => (
                  <MenuItem key={catId(c)} value={catId(c)}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('payables.account', 'From account')}</InputLabel>
              <Select
                label={t('payables.account', 'From account')}
                value={form.accountId || ''}
                onChange={(e) => set('accountId', e.target.value)}
              >
                <MenuItem value="">{t('payables.none', 'None')}</MenuItem>
                {(accounts || []).map((a) => (
                  <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <TextField
            label={t('payables.notes', 'Notes')}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <FormControlLabel
            control={<Switch checked={form.autopay} onChange={(e) => set('autopay', e.target.checked)} />}
            label={t('payables.autopay', 'Autopay reminder')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('payables.cancel', 'Cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : t('payables.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PayablesPage = () => {
  const { t, i18n } = useTranslation();
  const { formatAmount } = useCurrency();
  const { categories = [] } = useBudget();
  const { accounts = [] } = useAccount();
  const { hasFeature } = useSubscription();
  const unlocked = hasFeature('payables');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [payCategory, setPayCategory] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const showSnack = (message, severity = 'success') =>
    setSnack({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [monthRes, listRes, sugRes] = await Promise.all([
        api.get('/payables/month', { params: { year, month } }),
        api.get('/payables'),
        api.get('/payables/suggestions'),
      ]);
      setData(monthRes.data?.data || monthRes.data);
      setTemplates(listRes.data?.data || listRes.data || []);
      setSuggestions(sugRes.data?.data || sugRes.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load payables');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { if (unlocked) load(); }, [load, unlocked]);

  const shiftMonth = (delta) => {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  const period = data?.period || `${year}-${String(month).padStart(2, '0')}`;

  const items = useMemo(() => {
    const list = [...(data?.items || [])];
    list.sort((a, b) => {
      const s = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
      if (s !== 0) return s;
      return new Date(a.dueOn) - new Date(b.dueOn);
    });
    return list;
  }, [data]);

  const templateById = useMemo(() => {
    const m = {};
    templates.forEach((p) => { m[String(p._id)] = p; });
    return m;
  }, [templates]);

  const saveForm = async (body) => {
    if (body._id) {
      const { _id, ...patch } = body;
      await api.patch(`/payables/${_id}`, patch);
      showSnack(t('payables.updated', 'Payable updated'));
    } else {
      await api.post('/payables', body);
      showSnack(t('payables.created', 'Payable added'));
    }
    setFormOpen(false);
    setEditing(null);
    await load();
  };

  const markPaid = async (item, categoryId) => {
    if (!item.payableId) return;
    setBusyId(item.id);
    try {
      await api.post(`/payables/${item.payableId}/pay`, {
        period: item.period || period,
        amount: item.amount,
        categoryId: categoryId || item.categoryId || undefined,
        accountId: item.accountId || undefined,
      });
      showSnack(t('payables.markedPaid', 'Marked as paid — saved as an expense'));
      setPayTarget(null);
      await load();
    } catch (err) {
      if (err.response?.data?.code === 'category_required') {
        setPayTarget(item);
        setPayCategory('');
      } else {
        showSnack(err.response?.data?.message || 'Could not mark paid', 'error');
      }
    } finally {
      setBusyId(null);
    }
  };

  const unpay = async (item) => {
    if (!item.payableId) return;
    setBusyId(item.id);
    try {
      await api.post(`/payables/${item.payableId}/unpay`, { period: item.period || period });
      showSnack(t('payables.undone', 'Payment undone'));
      await load();
    } catch (err) {
      showSnack(err.response?.data?.message || 'Could not undo', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const archive = async (item) => {
    if (!item.payableId) return;
    setBusyId(item.id);
    try {
      await api.delete(`/payables/${item.payableId}`);
      showSnack(t('payables.archived', 'Removed from payables'));
      await load();
    } catch (err) {
      showSnack(err.response?.data?.message || 'Could not remove', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const importItem = async (src) => {
    const dueDay = src.dueDay || (src.dueOn ? new Date(src.dueOn).getDate() : 1);
    const payload = {
      name: src.name,
      amount: src.amount,
      dueDay,
      kind: src.kind || 'bill',
      categoryId: src.categoryId,
      accountId: src.accountId,
    };
    if (src.type === 'recurring' || src.source === 'recurring') {
      payload.linkedRecurringId = src.linkedRecurringId || src.sourceId;
      payload.kind = 'subscription';
    }
    if (src.type === 'debt' || src.source === 'debt') {
      payload.linkedDebtId = src.linkedDebtId || src.sourceId;
      payload.kind = 'debt';
    }
    if (src.source === 'split') {
      payload.linkedSplitId = src.sourceId;
      payload.kind = 'split';
    }
    setBusyId(src.id || src.linkedRecurringId || src.linkedDebtId);
    try {
      await api.post('/payables/import', payload);
      showSnack(t('payables.imported', 'Added to payables'));
      await load();
    } catch (err) {
      showSnack(err.response?.data?.message || 'Could not import', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const cov = data?.coverage || {};
  const palette = coverageSx[cov.status] || coverageSx.green;
  const totals = data?.totals || {};

  if (!unlocked) {
    return (
      <LockedFeature
        feature="payables"
        ctaPlan="premium"
        preview="overlay"
        promoText={t('payables.locked', 'Payables is a Premium feature — see what must leave this month')}
      >
        <Box sx={{ minHeight: 360, borderRadius: 3, bgcolor: 'action.hover' }} />
      </LockedFeature>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 8 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={800}>{t('payables.title', 'Payables')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('payables.subtitle', 'What must leave this month — and whether it is covered')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={() => shiftMonth(-1)} aria-label="previous month"><ChevronLeftIcon /></IconButton>
          <Typography variant="subtitle1" fontWeight={700} sx={{ minWidth: isMobile ? 120 : 180, textAlign: 'center' }}>
            {monthLabel(year, month, i18n.language)}
          </Typography>
          <IconButton onClick={() => shiftMonth(1)} aria-label="next month"><ChevronRightIcon /></IconButton>
          <Tooltip title={t('payables.refresh', 'Refresh')}>
            <IconButton onClick={load}><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditing(null); setFormOpen(true); }}>
            {t('payables.add', 'Add payable')}
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Box
        sx={{
          p: 2.5,
          borderRadius: 3,
          background: palette.bg,
          border: `1px solid ${palette.border}`,
        }}
      >
        {loading && !data ? (
          <CircularProgress size={22} />
        ) : (
          <Stack spacing={1.25}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Typography variant="h4" fontWeight={800}>
                {formatAmount(totals.remaining || 0)}
                <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1, fontWeight: 600 }}>
                  {t('payables.remaining', 'remaining')}
                </Typography>
              </Typography>
              <Chip
                label={
                  cov.status === 'red'
                    ? `${t('payables.coverageRed', 'Short')} ${formatAmount(cov.uncovered || 0)}`
                    : cov.status === 'amber'
                      ? t('payables.coverageAmber', 'Tight')
                      : t('payables.coverageGreen', 'Covered')
                }
                sx={{ fontWeight: 800, color: palette.fg, bgcolor: 'transparent', border: `1px solid ${palette.border}` }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {t('payables.liquid', 'Liquid cash')} {formatAmount(cov.liquid || 0)}
              {' · '}
              {t('payables.expectedIncome', 'Expected income')} {formatAmount(cov.expectedIncome || 0)}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip size="small" label={`${t('payables.paid', 'Paid')} ${formatAmount(totals.paid || 0)}`} />
              <Chip size="small" color="error" variant={totals.overdue ? 'filled' : 'outlined'} label={`${t('payables.overdue', 'Overdue')} ${formatAmount(totals.overdue || 0)}`} />
              <Chip size="small" color="warning" variant={totals.dueSoon ? 'filled' : 'outlined'} label={`${t('payables.dueSoon', 'Due soon')} ${formatAmount(totals.dueSoon || 0)}`} />
            </Stack>
          </Stack>
        )}
      </Box>

      {suggestions.length > 0 && (
        <Box sx={{ p: 2, borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            {t('payables.suggestions', 'Suggested from your accounts')}
          </Typography>
          <Stack spacing={1}>
            {suggestions.map((s) => (
              <Stack key={`${s.type}-${s.linkedDebtId || s.linkedRecurringId || s.name}`} direction="row" alignItems="center" gap={1} flexWrap="wrap">
                <Box sx={{ flex: 1, minWidth: 140 }}>
                  <Typography fontWeight={600}>{s.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatAmount(s.amount)} · {t(`payables.kinds.${s.kind}`, s.kind)} · {t('payables.dueDay', 'Due day')} {s.dueDay}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<PlaylistAddIcon />}
                  disabled={Boolean(busyId)}
                  onClick={() => importItem(s)}
                >
                  {t('payables.import', 'Add')}
                </Button>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {loading && !items.length ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Alert severity="info">
          {t('payables.empty', 'Nothing due this month. Add a bill, subscription, or one-off.')}
        </Alert>
      ) : (
        <Stack spacing={1}>
          {items.map((item) => {
            const chip = statusChip[item.status] || statusChip.upcoming;
            const tpl = item.payableId ? templateById[String(item.payableId)] : null;
            const busy = busyId === item.id;
            return (
              <Box
                key={item.id}
                sx={{
                  p: 1.75,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: item.status === 'paid' ? 'action.hover' : 'background.paper',
                  opacity: item.status === 'paid' ? 0.78 : 1,
                }}
              >
                <Stack direction="row" alignItems="flex-start" gap={1} flexWrap="wrap">
                  <Box sx={{ flex: 1, minWidth: 160 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                      <Typography fontWeight={700}>{item.name}</Typography>
                      <Chip size="small" label={t(`payables.kinds.${item.kind}`, item.kind)} />
                      <Chip size="small" color={chip.color} label={t(chip.labelKey, item.status)} />
                      {item.virtual && <Chip size="small" variant="outlined" label={t('payables.virtual', 'From other tools')} />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                      {formatAmount(item.amount)}
                      {' · '}
                      {item.dueOn
                        ? new Date(item.dueOn).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
                        : ''}
                      {item.notes ? ` · ${item.notes}` : ''}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {item.canMarkPaid && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={busy ? <CircularProgress size={14} /> : <CheckCircleIcon />}
                        disabled={busy}
                        onClick={() => markPaid(item)}
                      >
                        {t('payables.markPaid', 'Mark paid')}
                      </Button>
                    )}
                    {item.virtual && (
                      <Button
                        size="small"
                        startIcon={<PlaylistAddIcon />}
                        disabled={busy}
                        onClick={() => importItem(item)}
                      >
                        {t('payables.track', 'Track in Payables')}
                      </Button>
                    )}
                    {item.status === 'paid' && item.payableId && (
                      <Tooltip title={t('payables.undo', 'Undo')}>
                        <IconButton size="small" disabled={busy} onClick={() => unpay(item)}>
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {item.payableId && tpl && (
                      <Tooltip title={t('payables.edit', 'Edit payable')}>
                        <IconButton
                          size="small"
                          onClick={() => { setEditing(tpl); setFormOpen(true); }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {item.payableId && (
                      <Tooltip title={t('payables.archive', 'Remove')}>
                        <IconButton size="small" disabled={busy} onClick={() => archive(item)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}

      <PayableFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSave={saveForm}
        initial={editing}
        categories={categories}
        accounts={accounts}
        t={t}
      />

      <Dialog open={Boolean(payTarget)} onClose={() => setPayTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('payables.pickCategory', 'Pick a category')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('payables.pickCategoryHelp', 'Marking paid writes a real expense, so a category is required.')}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>{t('payables.category', 'Category')}</InputLabel>
            <Select
              label={t('payables.category', 'Category')}
              value={payCategory}
              onChange={(e) => setPayCategory(e.target.value)}
            >
              {(categories || []).map((c) => (
                <MenuItem key={catId(c)} value={catId(c)}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayTarget(null)}>{t('payables.cancel', 'Cancel')}</Button>
          <Button
            variant="contained"
            disabled={!payCategory}
            onClick={() => markPaid(payTarget, payCategory)}
          >
            {t('payables.markPaid', 'Mark paid')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </Box>
  );
};

export default PayablesPage;
